// SpiralStair.js
import * as THREE from 'three';
import Walls from '../Walls';
import MaterialUtils from '../../helpers/MaterialUtils';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export default class BespokeSpiralStair {
    constructor(scene, model, mainSceneSetup) {
        this.scene = scene;
        this.model = model;
        this.mainSceneSetup = mainSceneSetup;
        this.treadGroup = new THREE.Group();
        this.scene.add(this.treadGroup);

        // Initialize walls and load landing
        this.walls = new Walls(this.scene, this.createWallMaterial());
        const type = this.constructor.name === 'SpiralStair' ? 'Basic-Spiral' : 'Bespoke-Spiral';
        
        // Initialize other properties
        this.numTreads = 14;
        this.rotationAngle = 21;
        this.radius = 0;
        this.treadHeight = 0.18;
        this.scaleFactor = 2.4;
        this.mirror = false;
        this.topYPosition = 2.5;

        this.calculateTreadModelHeight();
        
        this.ready = this.initializeStair();
    }
    
    async initializeStair() {
        try {
            await this.walls.loadSpiralLanding(this.constructor.name === 'SpiralStair' ? 'Basic-Spiral' : 'Bespoke-Spiral');
            this.createStaircase(this.numTreads);
            await this.updateWallsWithBoundingBox();
    
            const boundingBox = new THREE.Box3().setFromObject(this.treadGroup);
            if (!boundingBox.isEmpty()) {
                this.isInitialized = true;
            } else {
                console.error('Bounding box not properly initialized');
            }
        } catch (error) {
            console.error("Error during spiral stair initialization:", error);
        }
    }
    async loadSpiralLanding(type) {
        const loader = new GLTFLoader();
        const landingPath = type === 'Basic-Spiral' 
            ? 'glTFModels/Spiral-Landing-dbl.gltf'
            : 'glTFModels/Bespoke-Spiral-Landing.gltf';
    
        try {
            console.log("Loading spiral landing for:", type);
            const gltf = await new Promise((resolve, reject) => {
                loader.load(
                    landingPath,
                    (gltf) => resolve(gltf),
                    undefined,
                    (error) => reject(error)
                );
            });
            
            if (gltf && gltf.scene) {
                this.spiralLanding = gltf.scene;
                this.spiralLanding.name = 'SpiralLanding';
                this.spiralLanding.visible = false;
                this.scene.add(this.spiralLanding);
                console.log("Spiral landing loaded successfully:", this.spiralLanding);
                return this.spiralLanding; // Return the landing
            }
            throw new Error('GLTF loading completed but scene was not available');
        } catch (error) {
            console.error('Error loading spiral landing:', error);
            this.spiralLanding = null;
            throw error; // Re-throw to handle in calling code
        }
    }

    calculateTreadModelHeight() {
        const treadBoundingBox = new THREE.Box3().setFromObject(this.model);
        const size = treadBoundingBox.getSize(new THREE.Vector3());
        this.treadModelHeight = size.y * this.scaleFactor; // Multiply by scale factor
        this.treadModelWidth = size.x * this.scaleFactor; // Multiply by scale factor
    }

    async initialize() {
        await this.ready;
        this.createStaircase(this.numTreads);
        console.log("Calling updateWallsWithBoundingBox from initialize");
        await this.updateWallsWithBoundingBox();

        const boundingBox = new THREE.Box3().setFromObject(this.treadGroup);
        if (!boundingBox.isEmpty()) {
            this.isInitialized = true;
        } else {
            console.error('Bounding box not properly initialized');
        }
        return Promise.resolve();
    }

    createStaircase(numTreads) {
        this.clearTreads(); // Remove old treads

        // Reset the group's position
        this.treadGroup.position.set(0, 0, 0);

        const angleIncrement = THREE.MathUtils.degToRad(this.rotationAngle);

        for (let i = 0; i < numTreads; i++) {
            const tread = this.model.clone();

            // Reverse angle calculation
            const angle = -(i + 1) * angleIncrement;

            const yOffset = this.topYPosition - i * this.treadHeight;

            // Mirror the individual tread along the X-axis
            tread.scale.x *= -1;

            // Position tread around the central pole
            tread.position.set(
                Math.sin(angle) * this.radius,
                yOffset,
                Math.cos(angle) * this.radius
            );

            // Rotate tread to face outward from the center
            tread.rotation.y = angle;

            // Enable shadows for better realism
            tread.castShadow = true;
            tread.receiveShadow = true;

            this.treadGroup.add(tread);
        }

        // Apply scaling factor
        this.treadGroup.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);

        // Apply mirroring to the entire group if needed
        if (this.mirror) {
            this.treadGroup.scale.x = -Math.abs(this.treadGroup.scale.x); // Flip on X-axis
        } else {
            this.treadGroup.scale.x = Math.abs(this.treadGroup.scale.x); // Ensure normal scale
        }

        // Compute bounding box after scaling
        const boundingBox = new THREE.Box3().setFromObject(this.treadGroup);
        const minY = boundingBox.min.y;

        // Adjust the group's position so that the bottom of the staircase is at y = 0
        this.treadGroup.position.y += -minY;
        console.log("Calling updateWallsWithBoundingBox from createStaircase");
        this.updateWallsWithBoundingBox(); // Update walls after the staircase is created
    }

    async updateWallsWithBoundingBox() {
        console.log("Starting updateWallsWithBoundingBox", new Error().stack);
        console.log("Walls object:", this.walls);
        console.log("Spiral landing exists:", !!this.walls.spiralLanding);

        // Calculate the bounding box of the treads only
        const treadBoundingBox = new THREE.Box3().setFromObject(this.treadGroup);
        /**
            this.treadGroup.children.forEach((tread) => {
                treadBoundingBox.expandByObject(tread);
            });
        */

        const size = treadBoundingBox.getSize(new THREE.Vector3());

        if (!size.x || !size.y || !size.z || !isFinite(size.x) || !isFinite(size.y) || !isFinite(size.z)) {
            console.error('Invalid or undefined bounding box dimensions:', size);
            return;
        }

        const { x: boxWidth, y: boxHeight, z: boxDepth } = size;
        const center = treadBoundingBox.getCenter(new THREE.Vector3());

        // Now position the spiralLanding independently
        if (this.walls.spiralLanding) {
            console.log("Found spiral landing", {
                beforeUpdate: {
                    position: this.walls.spiralLanding.position.clone(),
                    scale: this.walls.spiralLanding.scale.clone(),
                    visible: this.walls.spiralLanding.visible,
                    parent: this.walls.spiralLanding.parent.type
                },
                calculations: {
                    boundingBoxMaxY: treadBoundingBox.max.y,
                    scaleFactor: this.scaleFactor,
                    treadHeight: this.treadHeight
                }
            });
            // Set scale to match tread group
            const landingFlipFactor = this.mirror ? Math.abs(this.scaleFactor) : -Math.abs(this.scaleFactor);
            this.walls.spiralLanding.scale.set(landingFlipFactor, this.scaleFactor, this.scaleFactor * 1.05);

            // Calculate landing depth
            const landingBoundingBox = new THREE.Box3().setFromObject(this.walls.spiralLanding);
            const landingSize = landingBoundingBox.getSize(new THREE.Vector3());
            const landingDepth = landingSize.z;

            // Position spiralLanding independently
            // Get central pole position
            const centralPole = this.model.getObjectByName('cylinder');
            const polePosition = new THREE.Vector3();
            centralPole.getWorldPosition(polePosition);

            this.walls.spiralLanding.position.set(
                polePosition.x,
                treadBoundingBox.max.y - this.treadModelHeight + 0.42,
                polePosition.z + landingDepth 
            );

            this.walls.spiralLanding.visible = true;
            console.log("After update", {
                position: this.walls.spiralLanding.position.clone(),
                scale: this.walls.spiralLanding.scale.clone(),
                visible: this.walls.spiralLanding.visible,
                parent: this.walls.spiralLanding.parent.type
            });
        }

        // Now create a combined bounding box including both treads and spiralLanding
        const combinedBoundingBox = new THREE.Box3().setFromObject(this.treadGroup);
    if (this.walls.spiralLanding) {
        combinedBoundingBox.expandByObject(this.walls.spiralLanding);
    }

        const combinedSize = combinedBoundingBox.getSize(new THREE.Vector3());
        const combinedCenter = combinedBoundingBox.getCenter(new THREE.Vector3());

        // Update back wall geometry and position
        this.walls.backWall.geometry.dispose();
        const wallThickness = 3; // Adjust as necessary
        const backWallHeight = combinedSize.y; // Use the full height of staircase and landing
        this.walls.backWall.geometry = new THREE.BoxGeometry(combinedSize.x + 2, backWallHeight, wallThickness);
        this.walls.backWall.position.set(
            combinedCenter.x,
            combinedCenter.y - this.treadModelHeight + 0.54,
            combinedBoundingBox.max.z + wallThickness / 2 - 0.15
        );

        // Hide side wall if not needed
        this.walls.sideWall.visible = false;
        this.scene.needsUpdate = true; // Force the scene to re-render
    }

    toggleMirror() {
        this.mirror = !this.mirror;

        // Flip the staircase group on the X-axis
        this.treadGroup.scale.x = this.mirror
            ? -Math.abs(this.treadGroup.scale.x)
            : Math.abs(this.treadGroup.scale.x);

        // Flip the landing in the opposite direction since it starts flipped
        if (this.walls.spiralLanding) {
            const landingFlipFactor = this.mirror
                ? Math.abs(this.scaleFactor)  // If mirrored, use positive scale
                : -Math.abs(this.scaleFactor); // If not mirrored, use negative scale
            this.walls.spiralLanding.scale.set(landingFlipFactor, this.scaleFactor, this.scaleFactor);
        }

        // Recreate the staircase for the mirrored state
        this.createStaircase(this.numTreads);
    }

    updateStairTreads(numTreads) {
        this.numTreads = numTreads;
        this.createStaircase(numTreads);
    }

    updateScale(scaleFactor) {
        this.scaleFactor = scaleFactor;
        this.treadGroup.scale.set(scaleFactor, scaleFactor, scaleFactor);
        // Recalculate tread model dimensions after scaling
        this.calculateTreadModelHeight();
        this.updateWallsWithBoundingBox();
    }

    /**
        setupUIListeners() {
            const mirrorButton = document.getElementById('mirror-toggle');
            if (mirrorButton) {
                mirrorButton.addEventListener('click', () => {
                    this.toggleMirror();
                });
            }
    
            // Additional UI controls can be added here if needed
        }
    */

    clearTreads() {
        console.log("Starting spiral stair cleanup...");

        while (this.treadGroup.children.length > 0) {
            const child = this.treadGroup.children[0];
            this.treadGroup.remove(child);

            child.traverse((object) => {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(mat => mat.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            });
        }

        // Hide the landing when clearing
        if (this.walls.spiralLanding) {
            this.walls.spiralLanding.visible = false;
        }

        this.scene.remove(this.treadGroup);
        this.treadGroup = new THREE.Group();
        this.scene.add(this.treadGroup);

        console.log("Spiral stair cleanup completed");
    }

    createWallMaterial() {
        const textureLoader = new THREE.TextureLoader();
        const wallTexture = textureLoader.load('../../textures/cross-laminated-timber-clt-fir-2000-mm-architextures.jpg');
        wallTexture.wrapS = THREE.RepeatWrapping;
        wallTexture.wrapT = THREE.RepeatWrapping;
        wallTexture.repeat.set(4, 4);

        return new THREE.MeshStandardMaterial({
            map: wallTexture,
            side: THREE.DoubleSide,
        });
    }
}
